/* eslint-disable no-unused-vars */
/* eslint-disable react/no-string-refs */
import React from 'react';
import PropTypes from 'prop-types';
import {
  QuestionMarkCircleIcon,
  PhotographIcon,
  PresentationChartBarIcon,
  ChatAltIcon,
  LogoutIcon,
  FilmIcon,
} from '@heroicons/react/solid';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { uploadFile } from '../../../redux/actions/elearningActions';
import { useTranslation } from 'react-i18next';
import utils from './_utils';

export default function Upload(props) {
  const { showModal, setShowModal, addItems } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleImageUpload = async (e) => {
    if (!e?.target?.files?.length) {
      return;
    }

    const newFile = e.target.files[0];
    const fileLocation = await dispatch(uploadFile(newFile));

    addItems([
      {
        type: 'infographic',
        content: {
          imageUrl: fileLocation,
        },
        id: uuidv4(),
      },
    ]);
    setShowModal(false);
  };

  const handlePdfUpload = async (e) => {
    if (!e.target.files) {
      return;
    }

    const pdf = e.target.files[0];
    const images = await utils.createBlobPerPdfPage(pdf);
    const newItems = [];
    for await (const image of images) {
      const newFile = new File([image], 'image.png', { type: 'image/png' });
      const fileLocation = await dispatch(uploadFile(newFile));
      newItems.push({
        type: 'infographic',
        content: {
          imageUrl: fileLocation,
        },
        id: uuidv4(),
      });
    }

    addItems(newItems);
    setShowModal(false);
  };

  const handleMultipleChoiceUpload = () => {
    addItems([
      {
        id: uuidv4(),
        type: 'multiplechoice',
        content: {
          question: '',
          options: ['Option 1'],
          answers: [],
          explanation: '',
        },
      },
    ]);
    setShowModal(false);
  };

  const handleMp4 = async (e) => {
    if (!e?.target?.files?.length) {
      return;
    }

    const newFile = e.target.files[0];
    const fileLocation = await dispatch(uploadFile(newFile));

    addItems([
      {
        type: 'mp4',
        content: {
          url: fileLocation,
        },
        id: uuidv4(),
      },
    ]);
    setShowModal(false);
  };

  const handleSummary = () => {
    addItems([
      {
        id: uuidv4(),
        type: 'summary',
        content: {},
      },
    ]);
    setShowModal(false);
  };

  const handleOpenQuestion = () => {
    addItems([
      {
        id: uuidv4(),
        type: 'open',
        content: {
          question: '',
          explanation: '',
          answer: '',
        },
      },
    ]);
    setShowModal(false);
  };

  return (
    <>
      {showModal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative my-6 mx-auto max-w-3xl">
              {/* content */}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/* header */}
                <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                  <h3 className="text-3xl font-semibold">
                    {t('courses.what_to_add')}
                  </h3>
                  <button
                    className="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                    onClick={() => setShowModal(false)}
                    type="button"
                  />
                </div>
                {/* Upload Multiple choice */}
                <div className="relative p-6 grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    className="flex gap-2 bg-sky rounded-md text-white px-4 py-2 items-center hover:shadow-md"
                    onClick={handleMultipleChoiceUpload}
                  >
                    <QuestionMarkCircleIcon className="w-5 h-5" />
                    {t('courses.multiple_choice_question')}
                  </button>

                  {/* Upload Image */}
                  <div className="overflow-hidden relative w-full">
                    <button
                      type="button"
                      className="bg-pink rounded-md hover:bg-blue-light text-white font-bold py-2 px-4 w-full inline-flex items-center "
                    >
                      <PhotographIcon className="w-5 h-5" />
                      <span className="ml-2">{t('courses.image')}</span>
                    </button>
                    <input
                      className="cursor-pointer absolute block py-2 px-4 w-full opacity-0 pin-r pin-t -mt-12"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </div>

                  {/* Upload Powerpoint / PDF */}
                  <div className="overflow-hidden relative w-full">
                    <button
                      type="button"
                      className="bg-green rounded-md hover:bg-blue-light text-white font-bold py-2 px-4 w-full inline-flex items-center"
                    >
                      <PresentationChartBarIcon className="w-5 h-5" />
                      <span className="ml-2">
                        {t('courses.upload_powerpoint')}
                      </span>
                    </button>
                    <input
                      className="cursor-pointer absolute block py-2 px-4 w-full opacity-0 pin-r pin-t -mt-12"
                      type="file"
                      accept="pdf/*"
                      onChange={handlePdfUpload}
                    />
                  </div>

                  {/* Upload Open question */}
                  <button
                    type="button"
                    className="flex gap-2 bg-yellow rounded-md text-white px-4 py-2 items-center hover:shadow-md w-full"
                    onClick={handleOpenQuestion}
                  >
                    <ChatAltIcon className="w-5 h-5" />
                    {t('courses.open_question')}
                  </button>

                  {/* Upload mp4 */}
                  <div className="overflow-hidden relative w-full">
                    <button
                      type="button"
                      className="bg-green-500 rounded-md hover:bg-blue-light text-white font-bold py-2 px-4 w-full inline-flex items-center "
                    >
                      <FilmIcon className="w-5 h-5" />
                      <span className="ml-2">{t('courses.mp4_file')}</span>
                    </button>
                    <input
                      className="cursor-pointer absolute block py-2 px-4 w-full opacity-0 pin-r pin-t -mt-12"
                      type="file"
                      accept="video/mp4,video/x-m4v,video/*"
                      onChange={handleMp4}
                    />
                  </div>

                  {/* Add exit summary */}
                  <button
                    type="button"
                    className="flex gap-2 bg-red-700 rounded-md text-white px-4 py-2 items-center hover:shadow-md w-full"
                    onClick={handleSummary}
                  >
                    <LogoutIcon className="w-5 h-5" />
                    {t('courses.closing')}
                  </button>
                </div>

                {/* footer */}
                <div className="flex items-center justify-end p-4 border-t border-solid border-slate-200 rounded-b">
                  <button
                    className="text-red-500 font-bold uppercase px-6 py-2 text-md outline-none focus:outline-none mr-1 mb-1"
                    type="button"
                    onClick={() => setShowModal(false)}
                  >
                    {t('courses.button_cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black" />
        </>
      ) : null}
    </>
  );
}

Upload.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  addItems: PropTypes.func.isRequired,
};
